let _ = t => t,
  _t;
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAsyncEffect } from 'react-utils/hooks/useAsyncEffect';
import styled, { createGlobalStyle } from 'styled-components';
import I18n from 'I18n';
import { formatDuration } from 'video-data-lib/utils/time';
import { CONTROL_BAR_HEIGHT, CONTROL_BAR_OPAQUE_BACKGROUND, DEFAULT_PLAY_BUTTON_COLOR, MOBILE_BREAKPOINT_PX, SMALL_CONTROL_BAR_HEIGHT } from '../constants';
import SeekBarHoverPopover, { clampToRange } from './SeekBarHoverPopover';
import { fetchAndProcessVtt } from '../utils/vttThumbnails';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OPAQUE_PADDING = 5;
const SEEK_BAR_X_PADDING = 5;
const SEEK_BAR_X_PADDING_MOBILE = 5;
const SEEK_BAR_X_OFFSET = OPAQUE_PADDING + SEEK_BAR_X_PADDING;
const SEEK_BAR_HANDLE_SIZE = 14;
const CONTROL_BAR_LIGHT_GREY = 'rgba(220, 220, 220, 1)';
const SEEK_BAR_CONTROL_BOX_SHADOW = '0 0 4px 2px rgba(255, 255, 255, 0.5)';
const SEEK_BAR_KEYPRESS_STEP = 10;
const ControlBarOpaque = styled.div.withConfig({
  displayName: "SeekBar__ControlBarOpaque",
  componentId: "ryvxlw-0"
})(["position:absolute;bottom:", "px;left:", "px;right:", "px;height:", "px;background:", ";box-shadow:inset 0 -24px 8px -8px rgba(30,30,30,0.3),inset 0 -48px 16px -16px rgba(60,60,60,0.25);border-radius:5px;transition:opacity 250ms linear;pointer-events:none;@media (max-width:", "px) and (max-height:", "px){height:", "px;}"], OPAQUE_PADDING, OPAQUE_PADDING, OPAQUE_PADDING, CONTROL_BAR_HEIGHT + 5, CONTROL_BAR_OPAQUE_BACKGROUND, MOBILE_BREAKPOINT_PX, MOBILE_BREAKPOINT_PX, SMALL_CONTROL_BAR_HEIGHT + 7);
const SeekBarSliderHandle = styled.div.withConfig({
  displayName: "SeekBar__SeekBarSliderHandle",
  componentId: "ryvxlw-1"
})(["position:absolute;display:", ";width:", "px;height:", "px;transition:", ";transform:translateX(-50%) translateY(-25%);background-color:white;border-radius:50%;z-index:2;cursor:pointer;&:hover{box-shadow:", ";}"], ({
  isDragging
}) => isDragging ? 'block' : 'none', SEEK_BAR_HANDLE_SIZE, SEEK_BAR_HANDLE_SIZE, ({
  isDragging
}) => isDragging ? 'none' : 'left 250ms linear', SEEK_BAR_CONTROL_BOX_SHADOW);
const SeekBarContainer = styled.div.withConfig({
  displayName: "SeekBar__SeekBarContainer",
  componentId: "ryvxlw-2"
})(["position:absolute;display:flex;gap:2px;top:0;left:0;right:0;height:12px;margin:0 ", "px;padding:3px 0;z-index:2;cursor:pointer;pointer-events:initial;&:hover{z-index:5;}@media (max-width:", "px) and (max-height:", "px){margin:0 ", "px;}&:hover ", "{display:block;}"], SEEK_BAR_X_PADDING, MOBILE_BREAKPOINT_PX, MOBILE_BREAKPOINT_PX, SEEK_BAR_X_PADDING_MOBILE, SeekBarSliderHandle);
const ChapterSegment = styled.span.withConfig({
  displayName: "SeekBar__ChapterSegment",
  componentId: "ryvxlw-3"
})(["position:relative;flex-basis:", "%;top:50%;bottom:0;transform:translateY(-50%);overflow:hidden;background-color:", ";&:first-of-type{border-top-left-radius:3px;border-bottom-left-radius:3px;}&:last-of-type{border-top-right-radius:3px;border-bottom-right-radius:3px;}&:hover,&:focus{box-shadow:", ";}"], ({
  widthPercentage
}) => widthPercentage, CONTROL_BAR_LIGHT_GREY, SEEK_BAR_CONTROL_BOX_SHADOW);
const ChapterSegmentProgress = styled.div.withConfig({
  displayName: "SeekBar__ChapterSegmentProgress",
  componentId: "ryvxlw-4"
})(["position:absolute;top:0;bottom:0;left:0;background-color:", ";&[data-is-current='true']{transition:width 250ms linear;}"], ({
  progressColor
}) => progressColor);
const SeekBarGlobalStyles = createGlobalStyle(_t || (_t = _`
  .vjs-hubspot:not(.vjs-has-started) ${0}, .vjs-hubspot.vjs-controls-disabled ${0} {
    display: none;
  }
  .vjs-has-started.vjs-user-inactive.vjs-playing ${0} {
    opacity: 0
  }
`), ControlBarOpaque, ControlBarOpaque, ControlBarOpaque);
export const SeekBar = ({
  chapters,
  duration,
  player,
  playButtonColor,
  primaryPlaybackId
}) => {
  const seekBarRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  // tracks the currentTime user interacts to change to via slider, so we can keep the progress bar & handle stable as the player has to load to resume playback there
  const [requestedCurrentTime, setRequestedCurrentTime] = useState();
  const [mouseXOffset, setMouseXOffset] = useState(0);
  const [hoveredChapter, setHoveredChapter] = useState();
  const [focusedChapter, setFocusedChapter] = useState();
  const [thumbnailCues, setThumbnailCues] = useState([]);
  playButtonColor = playButtonColor || DEFAULT_PLAY_BUTTON_COLOR;
  if (playButtonColor && !playButtonColor.startsWith('#')) {
    playButtonColor = `#${playButtonColor}`;
  }
  const showHoverPopover = Boolean(isHovering || isDragging || focusedChapter);
  const effectiveCurrentTime = requestedCurrentTime !== null && requestedCurrentTime !== void 0 ? requestedCurrentTime : currentTime;
  useAsyncEffect(async () => {
    const unsetRequestedCurrentTime = () => {
      setRequestedCurrentTime(undefined);
    };
    player.on('seeked', unsetRequestedCurrentTime);
    if (primaryPlaybackId) {
      try {
        const vttCues = await fetchAndProcessVtt(primaryPlaybackId);
        setThumbnailCues(vttCues);
      } catch (error) {
        console.error('Error loading thumbnail storyboard VTT file:', error);
      }
    }
    return () => {
      player.off('seeked', unsetRequestedCurrentTime);
    };
  }, [player, primaryPlaybackId]);
  const getChapterAtTime = useCallback(time => {
    if (!chapters) {
      return;
    }
    return [...chapters].sort((a, b) => b.startTime - a.startTime).find(chapter => time >= chapter.startTime);
  }, [chapters]);
  const getEventOffsetX = useCallback(e => {
    if ('changedTouches' in e) {
      return e.changedTouches[0].clientX;
    }
    return e.clientX;
  }, []);
  const getTimeFromOffset = useCallback(xOffset => {
    if (!(seekBarRef !== null && seekBarRef !== void 0 && seekBarRef.current)) {
      return 0;
    }
    const seekBarRect = seekBarRef.current.getBoundingClientRect();
    const offsetRatio = (xOffset - SEEK_BAR_X_OFFSET) / seekBarRect.width;
    return clampToRange(offsetRatio * duration, 0, duration);
  }, [duration]);
  const isCurrentChapter = useCallback(chapter => {
    var _getChapterAtTime;
    return ((_getChapterAtTime = getChapterAtTime(effectiveCurrentTime)) === null || _getChapterAtTime === void 0 ? void 0 : _getChapterAtTime.title) === chapter.title;
  }, [effectiveCurrentTime, getChapterAtTime]);
  const getChapterDuration = useCallback(index => {
    if (!chapters) {
      return 0;
    }
    return chapters[index + 1] ? chapters[index + 1].startTime - chapters[index].startTime : duration - chapters[index].startTime;
  }, [chapters, duration]);
  const updateCurrentTime = useCallback(time => {
    player.currentTime(time);
    setRequestedCurrentTime(time);
    setTimeout(() => {
      setRequestedCurrentTime(undefined);
    }, 10000);
  }, [player]);
  const handleClick = useCallback(e => {
    setIsDragging(false);
    const time = getTimeFromOffset(getEventOffsetX(e));
    updateCurrentTime(time);
  }, [getTimeFromOffset, getEventOffsetX, updateCurrentTime]);
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setFocusedChapter(undefined);
  }, []);

  // mouseup events are not reliably fired if user drags significantly out of the iframe before releasing,
  // but usually are and this seems ideal vs a busy mousemove/mouseleave handler, as to not terminate the drag when user is only slighly out of frame
  const handleMouseUp = useCallback(e => {
    if (isDragging) {
      handleClick(e);
    }
    setIsDragging(false);
  }, [isDragging, handleClick]);
  const handleMouseMove = useCallback(e => {
    const clientX = getEventOffsetX(e);
    const time = getTimeFromOffset(clientX);
    const chapter = getChapterAtTime(time);
    setMouseXOffset(clientX);
    setHoveredChapter(chapter);
  }, [getChapterAtTime, getTimeFromOffset, getEventOffsetX]);
  const handleDragStart = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
  }, []);
  const handleDragEnd = useCallback(e => {
    setIsDragging(false);
    handleClick(e);
  }, [handleClick]);
  const handleSeekBarKeypress = useCallback(async e => {
    if (e.code === 'ArrowLeft' && currentTime >= SEEK_BAR_KEYPRESS_STEP) {
      updateCurrentTime(currentTime - SEEK_BAR_KEYPRESS_STEP);
    } else if (e.code === 'ArrowRight' && currentTime < duration - SEEK_BAR_KEYPRESS_STEP) {
      updateCurrentTime(currentTime + SEEK_BAR_KEYPRESS_STEP);
    } else if (e.code === 'Space' || e.code === 'Enter') {
      if (player.paused()) {
        await player.play();
      } else {
        player.pause();
      }
    }
  }, [currentTime, duration, player, updateCurrentTime]);
  const handleChapterKeypress = useCallback(async (chapter, e) => {
    if (e.code === 'Space' || e.code === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      updateCurrentTime(chapter.startTime);
      if (player.paused()) {
        await player.play();
      }
    }
  }, [player, updateCurrentTime]);
  const handleFocusChapter = useCallback(chapter => {
    setFocusedChapter(chapter);
  }, []);
  useEffect(() => {
    player.on('timeupdate', () => {
      setCurrentTime(player.currentTime());
    });
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleMouseMove);
    };
  }, [player, handleMouseUp, handleMouseMove]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(ControlBarOpaque, {
      className: "seek-bar-control-bar",
      children: [/*#__PURE__*/_jsx(SeekBarGlobalStyles, {}), /*#__PURE__*/_jsxs(SeekBarContainer, {
        ref: seekBarRef,
        "aria-label": I18n.text('videoPlayer.controls.seekBar'),
        "aria-valuemin": 0,
        "aria-valuemax": duration,
        "aria-valuenow": Math.floor(currentTime),
        "aria-valuetext": `${I18n.text('videoPlayer.controls.elapsedTime', {
          timeDisplay: formatDuration(currentTime * 1000)
        })}`,
        role: "slider",
        tabIndex: 0,
        onBlur: () => setFocusedChapter(undefined),
        onClick: handleClick,
        onKeyUp: handleSeekBarKeypress,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: [duration && /*#__PURE__*/_jsx(SeekBarHoverPopover, {
          chapter: hoveredChapter,
          focusedChapter: focusedChapter,
          duration: duration,
          time: focusedChapter ? focusedChapter.startTime : getTimeFromOffset(mouseXOffset),
          thumbnailCues: thumbnailCues,
          mouseXOffset: mouseXOffset,
          isVisible: showHoverPopover
        }), chapters !== null && chapters !== void 0 && chapters.length && duration ? chapters.map((chapter, i) => {
          const chapterDuration = getChapterDuration(i);
          let progressPercentage = 0;
          if (isCurrentChapter(chapter)) {
            progressPercentage = (effectiveCurrentTime - chapter.startTime) / chapterDuration * 100;
          } else if (effectiveCurrentTime > chapter.startTime) {
            progressPercentage = 100;
          }
          return (
            /*#__PURE__*/
            // @bend-expect trellis-tokenized
            _jsx(ChapterSegment, {
              "aria-label": `${I18n.text('videoPlayer.controls.chapter')}: ${chapter.title}`,
              tabIndex: 0,
              widthPercentage: chapterDuration / duration * 100,
              onMouseDown: e => e.preventDefault(),
              onFocus: () => handleFocusChapter(chapter),
              onKeyUp: e => handleChapterKeypress(chapter, e),
              children: /*#__PURE__*/_jsx(ChapterSegmentProgress, {
                "data-is-current": isCurrentChapter(chapter),
                progressColor: playButtonColor,
                style: {
                  width: `${progressPercentage}%`
                }
              })
            }, chapter.title)
          );
        }) :
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(ChapterSegment, {
          widthPercentage: 100,
          children: /*#__PURE__*/_jsx(ChapterSegmentProgress, {
            "data-is-current": true,
            progressColor: playButtonColor,
            style: {
              width: `${effectiveCurrentTime / duration * 100}%`
            }
          })
        }), duration &&
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(SeekBarSliderHandle, {
          draggable: true,
          isDragging: isDragging,
          onDragStart: handleDragStart,
          onDragEnd: handleDragEnd,
          onTouchStart: handleDragStart,
          style: {
            left: isDragging ? mouseXOffset - SEEK_BAR_X_OFFSET : `${effectiveCurrentTime / duration * 100}%`
          }
        })]
      })]
    })
  );
};